import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import CuoButton from "@/app/admin/_common/CuoButton";
import { LocationForm } from "./LocationForm";
import { useLocationFormFlow } from "./useLocationFormFlow";
import type { Location } from "@/app/_services/LocationService";
import { Building2 } from "lucide-react";

export const LocationDialog = ({
  defaultLocation,
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  defaultLocation?: Location | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const flow = useLocationFormFlow({
    defaultLocation,
    onSaved: () => {
      onSaved();
      onClose();
    },
  });
  const { control, errors, getValues, step, geocodeResult, isValidating, isSaving } = flow;

  const values = getValues();
  const typedAddress = [values.address_line_1, values.city, values.state, values.zip]
    .filter(Boolean)
    .join(", ");

  const isDuplicate = Boolean(geocodeResult?.existing_location);
  const isVerified = geocodeResult?.latitude != null && geocodeResult?.longitude != null;

  const confirmLabel = isDuplicate
    ? "Continue anyway"
    : isVerified
      ? "Use suggested address"
      : "Save anyway";

  const confirmAction = isVerified ? flow.handleConfirm : flow.handleSaveAnyway;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-full max-w-[440px] translate-x-0 translate-y-0 bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <Building2 className="size-4 flex-none text-muted-foreground" />
              {defaultLocation ? defaultLocation.name : "New Location"}
            </span>
          </DialogTitle>
          <DialogDescription>
            {step === "form"
              ? "Enter the venue's name and address."
              : "Review the suggested address before continuing."}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">
          <LocationForm
            control={control}
            errors={errors}
            step={step}
            geocodeResult={geocodeResult}
            typedAddress={typedAddress}
            confirmLabel={confirmLabel}
            onConfirm={confirmAction}
            onEdit={flow.handleBack}
            isSaving={isSaving}
          />
        </div>

        {step === "form" && (
          <DialogFooter>
            <CuoButton variant="outline" label="Cancel" onClick={onClose} />
            <CuoButton
              disableOnAsync={isValidating}
              variant="default"
              onClick={flow.handleContinue}
              label="Continue"
            />
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
